const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const decimal = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const clock = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

export type Direction = "up" | "down" | "flat";

/** Local wall-clock time for a seconds-epoch chart point, e.g. `01:14:24 PM`. */
export const formatClockTime = (secondsEpoch: number): string =>
  clock.format(secondsEpoch * 1_000);

export const formatCurrency = (value: number): string => usd.format(value);

/** Signed currency, e.g. `+$1,204.10` / `-$88.00`. */
export const formatSignedCurrency = (value: number): string => {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${usd.format(Math.abs(value))}`;
};

/** Signed percent, e.g. `+1.24%`. */
export const formatPercent = (value: number, digits = 2): string => {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
};

/** Plain two-decimal number, used for index levels that are not currency. */
export const formatDecimal = (value: number): string => decimal.format(value);

export const directionOf = (value: number): Direction => {
  if (value > 0) {
    return "up";
  }
  if (value < 0) {
    return "down";
  }
  return "flat";
};

export const directionColor = (
  direction: Direction,
): "green" | "red" | "gray" =>
  direction === "up" ? "green" : direction === "down" ? "red" : "gray";

/** CSS stroke for SVG / canvas, matching `directionColor`. */
export const directionStroke = (direction: Direction): string =>
  direction === "up"
    ? "var(--green-9)"
    : direction === "down"
      ? "var(--red-9)"
      : "var(--gray-11)";

/**
 * Broker money fields arrive as decimal strings. Parse defensively: a missing
 * or non-numeric field becomes `undefined` so a card can render a placeholder
 * rather than `NaN`.
 */
export const parseMoney = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};
